/** Privacy-friendly Umami analytics — marketing/landing pages only. */

export const UMAMI_OPT_OUT_KEY = "artlessdev.analytics.opt-out"
export const UMAMI_NOTICE_DISMISSED_KEY = "artlessdev.analytics.notice-dismissed"

export interface UmamiConfig {
  websiteId: string
  scriptUrl: string
  domains?: string
}

declare global {
  interface Window {
    umami?: {
      track: (event?: string | (() => Record<string, unknown>)) => void
    }
  }
}

export function getUmamiConfig(): UmamiConfig | null {
  const websiteId = import.meta.env.VITE_UMAMI_WEBSITE_ID?.trim()
  if (!websiteId) return null

  const scriptUrl =
    import.meta.env.VITE_UMAMI_SCRIPT_URL?.trim() || "https://cloud.umami.is/script.js"
  const domains = import.meta.env.VITE_UMAMI_DOMAINS?.trim()

  return domains ? { websiteId, scriptUrl, domains } : { websiteId, scriptUrl }
}

export function isDoNotTrackEnabled(): boolean {
  if (typeof navigator === "undefined") return false
  const dnt = navigator.doNotTrack ?? (navigator as Navigator & { msDoNotTrack?: string }).msDoNotTrack
  return dnt === "1" || dnt === "yes"
}

export function isAnalyticsOptedOut(): boolean {
  if (typeof window === "undefined") return false
  try {
    return window.localStorage.getItem(UMAMI_OPT_OUT_KEY) === "true"
  } catch {
    return false
  }
}

export function setAnalyticsOptOut(optOut: boolean): void {
  if (typeof window === "undefined") return
  try {
    if (optOut) {
      window.localStorage.setItem(UMAMI_OPT_OUT_KEY, "true")
    } else {
      window.localStorage.removeItem(UMAMI_OPT_OUT_KEY)
    }
  } catch {
    // Storage blocked — ignore.
  }
}

export function isAnalyticsNoticeDismissed(): boolean {
  if (typeof window === "undefined") return false
  try {
    return window.localStorage.getItem(UMAMI_NOTICE_DISMISSED_KEY) === "true"
  } catch {
    return false
  }
}

export function dismissAnalyticsNotice(): void {
  if (typeof window === "undefined") return
  try {
    window.localStorage.setItem(UMAMI_NOTICE_DISMISSED_KEY, "true")
  } catch {
    // Storage blocked — ignore.
  }
}

export function shouldLoadUmami(): boolean {
  return getUmamiConfig() !== null && !isAnalyticsOptedOut() && !isDoNotTrackEnabled()
}

let scriptPromise: Promise<void> | null = null

export function loadUmamiScript(config: UmamiConfig): Promise<void> {
  if (typeof document === "undefined") return Promise.resolve()
  if (document.querySelector(`script[data-website-id="${config.websiteId}"]`)) {
    return Promise.resolve()
  }

  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script")
      script.defer = true
      script.async = true
      script.src = config.scriptUrl
      script.dataset.websiteId = config.websiteId
      script.dataset.autoTrack = "false"
      if (config.domains) {
        script.dataset.domains = config.domains
      }
      script.onload = () => resolve()
      script.onerror = () => {
        scriptPromise = null
        reject(new Error("Failed to load Umami analytics script"))
      }
      document.head.appendChild(script)
    })
  }

  return scriptPromise
}

export function trackUmamiPageview(path?: string): void {
  if (!shouldLoadUmami()) return
  const umami = window.umami
  if (!umami) return

  if (path) {
    umami.track(() => ({ url: path }))
    return
  }

  umami.track()
}
